import {
  useState, useMemo, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';

// UseCases
import {
  transformDirectEditActions,
  buildSelectClassOptimisticPatch,
} from './tableDirectEditUseCase';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';
import { normalizeUrl } from '../../utils/url';

// Services
import { useDirectEditCellValueMutation } from '../../services/directEdit/directEditMutation';

// Store
import { useFormPanelContextOptional } from '../../store/formPanel/FormPanelContext';

// Components
import useToast from '../../components/molecules/Toast/useToast';

/**
 * Hook to manage direct edit actions (actionChoice)
 * @param {object} props
 * @param {TableCellValueObjectCustom} props.cellValue - Cell value with directEdit config
 * @param {ApiManagerTableColumns} props.column - Column configuration
 * @param {(editing: boolean) => void} [props.onEditingChange] - Called when the actions popover opens or closes
 * @param {boolean} [props.initialOpen=false] - Open the actions popover on mount
 * @returns {{
 *   handleActionByType: (action: TableDirectEditFlatAction, type: string) => void,
 *   actionsIsOpen: boolean,
 *   setActionsIsOpen: (isOpen: boolean) => void,
 *   hasActions: boolean,
 *   actions: Array<{lib: string, options: Array<TableDirectEditFlatAction>}>,
 *   isMutationLoading: boolean,
 * }} The direct edit actions state and handlers
 */
const useTableDirectEditActions = ({
  cellValue, column, onEditingChange, initialOpen = false,
}) => {
  const [actionsIsOpen, setActionsIsOpen] = useState(Boolean(initialOpen));
  const formPanel = useFormPanelContextOptional();
  const { notify } = useToast();
  const { t } = useTranslation();

  const actionChoice = cellValue?.directEdit?.actionChoice;
  const hasActions = Boolean(actionChoice?.actions && actionChoice.actions.length > 0);

  const actions = useMemo(() => {
    if (!hasActions || !actionChoice?.actions) {
      return [];
    }

    const transformedActions = transformDirectEditActions(actionChoice.actions);
    return groupOptionsByOptGroup(transformedActions);
  }, [hasActions, actionChoice?.actions]);

  /**
   * Close the popover and tell the user the API did not provide the action URL
   */
  const notifyMissingUrl = () => {
    setActionsIsOpen(false);
    notify({ message: t('toast.directEdit.missingUrl'), type: 'info' });
  };

  /**
   * Handle the open tab action
   * @param {ApiManagerTableDirectEditActionOpenTabObject} action - The action to handle
   */
  const handleOpenTab = (action) => {
    if (!action.basicUrl) {
      notifyMissingUrl();
      return;
    }
    const normalizedUrl = normalizeUrl(action.basicUrl);
    window.open(normalizedUrl, '_blank');
    setActionsIsOpen(false);
  };

  const { mutate: mutateCellValue, isPending: isMutationLoading } = useDirectEditCellValueMutation();

  // Single signal to the outside: the cell is "busy" while the actions popover is
  // open OR while the mutation is in flight (keeps hover preview suppressed across
  // the popover → pending-save transition).
  const isBusy = actionsIsOpen || isMutationLoading;
  useEffect(() => {
    onEditingChange?.(isBusy);
  }, [isBusy, onEditingChange]);

  /**
   * Handle the open form modal action: open formUrl, then POST fieldsToUpdate[0].value to setAttributeUrl
   * @param {ApiManagerTableDirectEditActionOpenFormModalObject} action - The action to handle
   */
  const handleOpenFormModal = (action) => {
    if (!action.formUrl) {
      notifyMissingUrl();
      return;
    }

    if (!formPanel?.openFormPanel) {
      setActionsIsOpen(false);
      return;
    }

    setActionsIsOpen(false);

    const onChildSuccess = (/** @type {ApiFormFieldsToUpdateItem[]} */ fieldsToUpdate) => {
      const field = fieldsToUpdate?.[0];
      if (!cellValue?.id || !column?.id || !action.setAttributeUrl || !field?.value) return;

      mutateCellValue({
        url: action.setAttributeUrl,
        value: field.value,
        rowId: cellValue.id,
        columnId: column.id,
        cellPatch: buildSelectClassOptimisticPatch(cellValue, {
          label: field.displayedValue ?? String(field.value),
          color: null,
        }),
      });
    };

    formPanel.openFormPanel(action.formUrl, onChildSuccess);
  };

  /**
   * Handle clear cell value action
   * @param {ApiManagerTableDirectEditActionClearCellValueObject} action - The action to handle
   */
  const handleClearCellValue = (action) => {
    if (!action.deleteUrl) {
      notifyMissingUrl();
      return;
    }
    if (!cellValue?.id || !column?.id) return;
    setActionsIsOpen(false);
    mutateCellValue({
      url: action.deleteUrl,
      value: '',
      rowId: cellValue.id,
      columnId: column.id,
      cellPatch: {
        value: { string: '' },
        backgroundColor: null,
      },
    });
  };

  /**
   * Handle replace cell value action
   * @param {ApiManagerTableDirectEditActionReplaceCellValueObject} action - The action to handle
   */
  const handleReplaceCellValue = (action) => {
    if (!action.setAttributeUrl) {
      notifyMissingUrl();
      return;
    }
    if (!cellValue?.id || !column?.id) return;
    setActionsIsOpen(false);
    mutateCellValue({
      url: action.setAttributeUrl,
      value: action.value,
      rowId: cellValue.id,
      columnId: column.id,
      cellPatch: {
        value: { string: action.lib },
      },
    });
  };

  /**
   * Handle the action by type
   * @param {TableDirectEditFlatAction} action - The action to handle
   * @param {string} type - The action type
   */
  const handleActionByType = (action, type) => {
    switch (type) {
      case 'openTab': {
        const actionOpenTab = /** @type {ApiManagerTableDirectEditActionOpenTabObject} */ (action);
        handleOpenTab(actionOpenTab);
        break;
      }
      case 'openFormModal': {
        const actionOpenFormModal = /** @type {ApiManagerTableDirectEditActionOpenFormModalObject} */ (action);
        handleOpenFormModal(actionOpenFormModal);
        break;
      }
      case 'clearCellValue': {
        const actionClearCellValue = /** @type {ApiManagerTableDirectEditActionClearCellValueObject} */ (action);
        handleClearCellValue(actionClearCellValue);
        break;
      }
      case 'replaceCellValue': {
        const actionReplaceCellValue = /** @type {ApiManagerTableDirectEditActionReplaceCellValueObject} */ (action);
        handleReplaceCellValue(actionReplaceCellValue);
        break;
      }
      default:
        // eslint-disable-next-line no-console
        console.warn(`Action type ${type} not supported`);
        break;
    }
  };

  return {
    handleActionByType,
    actionsIsOpen,
    setActionsIsOpen,
    hasActions,
    actions,
    isMutationLoading,
  };
};

export default useTableDirectEditActions;
